import {
    CLASSIC_HEIGHT,
    CLASSIC_SVG_GROUND_Y,
    CLASSIC_SVG_UNITS_PER_UNIT,
    type FigureFrame,
} from "./mat3d-figure-types";

type RectLike = { left: number; top: number; width: number; height: number };

export type FigureCanvasPlacement = {
    /** Lage der Leinwand im Link (CSS-Pixel, ohne dessen Hover-Skalierung). */
    left: number;
    top: number;
    width: number;
    height: number;
    frame: FigureFrame;
    visible: boolean;
};

/** Hoehe der SVG-viewBox von `MatFigure` (130 × 165). */
const SVG_VIEWBOX_HEIGHT = 165;

/** Ueberstand der Leinwand fuer Spruenge, als Anteil der SVG-Hoehe: oben, seitlich, unten. */
export type CanvasBleed = { top: number; side: number; bottom: number };

/** Startseite (Labor `index.html`, Slot `home`: `data-bleed="0.6,0.35,0.05"`). */
export const HOME_BLEED: CanvasBleed = { top: 0.6, side: 0.35, bottom: 0.05 };

/**
 * KI-Technologie-Hero (Labor `index.html`, Slot `ki`: `data-bleed="0.25,0.25,0.05"`).
 * Die Figur ist dort 240 bis 280 px hoch statt 155 px; derselbe Anteil wie auf
 * der Startseite haette die Leinwand 168 px ueber die Figur hinaus gezogen.
 */
export const KI_BLEED: CanvasBleed = { top: 0.25, side: 0.25, bottom: 0.05 };

/**
 * Legt die Leinwand der 3D-Figur deckungsgleich ueber die SVG-Figur: gleiche
 * Mitte, gleiche Fusslinie, gleiche Figurhoehe, plus Ueberstand fuer Spruenge.
 *
 * Gemessen wird mit `getBoundingClientRect`, das die Hover-Skalierung des
 * Links (`hover:scale-110`, Puls) enthaelt. Die Leinwand liegt IM Link und
 * skaliert mit; darum rechnet die Funktion die Skalierung heraus. `anchor…`
 * ist das Element, das die Leinwand traegt (Startseite: der Link, KI-Seite:
 * der Slot).
 */
export const placeFigureCanvas = ({
    anchorRect,
    anchorLayoutWidth,
    svgRect,
    bleed = HOME_BLEED,
}: {
    anchorRect: RectLike;
    anchorLayoutWidth: number;
    svgRect: RectLike;
    bleed?: CanvasBleed;
}): FigureCanvasPlacement => {
    const scale = anchorLayoutWidth > 0 && anchorRect.width > 0 ? anchorRect.width / anchorLayoutWidth : 1;
    const svgLeft = (svgRect.left - anchorRect.left) / scale;
    const svgTop = (svgRect.top - anchorRect.top) / scale;
    const svgWidth = svgRect.width / scale;
    const svgHeight = svgRect.height / scale;
    const top = bleed.top * svgHeight;
    const side = bleed.side * svgHeight;
    const unitPx = svgHeight / SVG_VIEWBOX_HEIGHT;
    return {
        left: svgLeft - side,
        top: svgTop - top,
        width: svgHeight > 0 ? svgWidth + 2 * side : 0,
        height: svgHeight * (1 + bleed.top + bleed.bottom),
        frame: {
            figurePx: CLASSIC_HEIGHT * CLASSIC_SVG_UNITS_PER_UNIT * unitPx,
            feetPx: top + CLASSIC_SVG_GROUND_Y * unitPx,
        },
        visible: svgHeight > 0 && svgWidth > 0,
    };
};
